<template>
  <button
    class="voice-btn"
    :class="{
      'voice-btn--recording': isRecording,
      'voice-btn--pending': isPending,
      'voice-btn--error': !!error,
    }"
    :disabled="isPending"
    :aria-label="buttonLabel"
    :aria-pressed="isRecording"
    @click="toggle"
  >
    <span class="voice-icon" aria-hidden="true">{{ icon }}</span>
    <span class="voice-label">{{ buttonLabel }}</span>
  </button>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranscribeMutation } from '../queries/transcribe.ts';

const emit = defineEmits<{
  transcribed: [code: string];
}>();

const { mutateAsync, isPending, error } = useTranscribeMutation();

const isRecording = ref(false);
let mediaRecorder: MediaRecorder | null = null;
let chunks: Blob[] = [];

// VAD state
const CALIBRATION_MS = 500;   // measure ambient noise for this long before listening
const SILENCE_TIMEOUT_MS = 1500; // stop after this much silence post-speech
const SPEECH_THRESHOLD_FACTOR = 1.4; // speech must be this many times louder than ambient
const POLL_INTERVAL_MS = 30;

let audioContext: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let vadInterval: ReturnType<typeof setInterval> | null = null;
let silenceTimer: ReturnType<typeof setTimeout> | null = null;
let speechDetected = false;

const icon = computed(() => {
  if (isPending.value) return '⏳';
  if (isRecording.value) return '⏹';
  if (error.value) return '⚠️';
  return '🎤';
});

const buttonLabel = computed(() => {
  if (isPending.value) return 'Transcribing…';
  if (isRecording.value) return 'Stop recording';
  return 'Voice input';
});

/**
 * Root Mean Square — a measure of signal power that gives a perceptually
 * reasonable approximation of loudness from raw PCM sample data.
 */
function getRms(analyserNode: AnalyserNode): number {
  const buf = new Float32Array(analyserNode.fftSize);
  analyserNode.getFloatTimeDomainData(buf);
  const sumSq = buf.reduce((acc, v) => acc + v * v, 0);
  return Math.sqrt(sumSq / buf.length);
}

function stopVad(): void {
  if (vadInterval !== null) clearInterval(vadInterval);
  if (silenceTimer !== null) clearTimeout(silenceTimer);
  vadInterval = null;
  silenceTimer = null;
  audioContext?.close();
  audioContext = null;
  analyser = null;
  speechDetected = false;
}

function startVad(stream: MediaStream, onSilence: () => void): void {
  audioContext = new AudioContext();
  analyser = audioContext.createAnalyser();
  analyser.fftSize = 1024;
  audioContext.createMediaStreamSource(stream).connect(analyser);

  const analyserNode = analyser;

  // Calibrate: sample ambient noise level for CALIBRATION_MS, then start watching
  const calibrationSamples: number[] = [];
  const calibrationEnd = Date.now() + CALIBRATION_MS;

  vadInterval = setInterval(() => {
    const rms = getRms(analyserNode);

    if (Date.now() < calibrationEnd) {
      calibrationSamples.push(rms);
      return;
    }

    // Compute threshold once calibration samples are ready
    const ambient = calibrationSamples.length
      ? calibrationSamples.reduce((a, b) => a + b, 0) / calibrationSamples.length
      : 0;
    const threshold = ambient * SPEECH_THRESHOLD_FACTOR;

    if (rms > threshold) {
      speechDetected = true;
      if (silenceTimer !== null) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
      }
    } else if (speechDetected && silenceTimer === null) {
      silenceTimer = setTimeout(() => {
        onSilence();
      }, SILENCE_TIMEOUT_MS);
    }
  }, POLL_INTERVAL_MS);
}

async function startRecording(): Promise<void> {
  chunks = [];
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  mediaRecorder = new MediaRecorder(stream);

  mediaRecorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  mediaRecorder.onstop = async () => {
    stream.getTracks().forEach((t) => t.stop());
    stopVad();

    const blob = new Blob(chunks, { type: mediaRecorder?.mimeType ?? 'audio/webm' });
    try {
      const result = await mutateAsync(blob);
      emit('transcribed', result.code);
    } catch (err) {
      console.error('[voice] Transcription failed:', err);
    }
  };

  mediaRecorder.start();
  isRecording.value = true;

  startVad(stream, () => stopRecording());
}

function stopRecording(): void {
  mediaRecorder?.stop();
  isRecording.value = false;
}

function toggle(): void {
  if (isRecording.value) {
    stopRecording();
  } else {
    startRecording().catch((err: unknown) => {
      console.error('[voice] Failed to start recording:', err);
    });
  }
}
</script>

<style scoped>
.voice-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  border: 2px solid #d1d5db;
  border-radius: 10px;
  background: #f9fafb;
  cursor: pointer;
  width: 280px;
  height: 180px;
  font-size: 1.1rem;
  font-weight: 600;
  transition: background 0.15s, border-color 0.15s;
}

.voice-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.voice-btn--recording {
  border-color: #dc2626;
  background: #fee2e2;
  animation: pulse 1.2s ease-in-out infinite;
}

.voice-btn--error {
  border-color: #f59e0b;
  background: #fffbeb;
}

.voice-icon {
  font-size: 3.5rem;
  line-height: 1;
}

@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
  50% { box-shadow: 0 0 0 16px rgba(220, 38, 38, 0); }
}

@media (prefers-color-scheme: dark) {
  .voice-btn {
    background: #1f2937;
    border-color: #374151;
    color: #f9fafb;
  }

  .voice-btn--recording {
    border-color: #dc2626;
    background: #450a0a;
  }

  .voice-btn--error {
    border-color: #f59e0b;
    background: #1c1708;
  }
}
</style>
